import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Icon } from '../components/icons';
import { findRoute } from '../navigation/routes';
import {
  FLOW_PHASES,
  FLOW_PHASE_COUNT,
  FLOW_STEPS,
  FlowPhaseId,
  FlowStepId,
  flowPhaseById,
  flowStepById,
  phaseOfStep,
  phasePath,
} from './flowSteps';
import { phaseState, statusOf } from './flowStatus';
import { useLinkedInFlow } from './useLinkedInFlow';

export interface FlowStepBarProps {
  /** Prozessschritt der Seite (mit Status-Banner). */
  stepId?: FlowStepId;
  /** Alternativ: nur die Phase, z. B. fuer Reiter ohne Prozessstatus (Geplante Posts). */
  phaseId?: FlowPhaseId;
  /** Zusaetzliche Aktionen rechts in der Leiste. */
  extraActions?: React.ReactNode;
}

/**
 * Schrittleiste am Kopf jeder LinkedIn-Seite: welche Phase, welche Reiter
 * gehoeren dazu, was fehlt noch, wohin geht es weiter. Die Phasen sind die
 * Menueeintraege; die Reiter darunter sind die einzelnen Schritte.
 */
export const FlowStepBar: React.FC<FlowStepBarProps> = ({ stepId, phaseId, extraActions }) => {
  const { status, loading, loaded } = useLinkedInFlow();
  const { pathname } = useLocation();
  const step = stepId ? flowStepById(stepId) : undefined;
  const phase = step ? phaseOfStep(step.id) : flowPhaseById(phaseId ?? 'foundation');
  const st = step ? statusOf(status, step.id) : undefined;
  const prevPhase = FLOW_PHASES[phase.index - 2];
  const nextPhase = FLOW_PHASES[phase.index];
  const nextStep = step ? FLOW_STEPS[step.index] : undefined;

  const tabs = [
    ...phase.steps.map((id) => {
      const s = statusOf(status, id);
      return {
        key: id, path: s.step.path, label: s.step.label, title: s.step.description,
        done: loaded && s.state === 'done', current: id === stepId,
      };
    }),
    ...phase.extraTabs.map((t) => ({
      // Zusatzreiter haben keinen eigenen Erklaertext; der Tooltip kommt aus der Routen-Registry.
      key: t.path, path: t.path, label: t.label, title: findRoute(t.path)?.description, done: false,
      current: !stepId && (pathname === t.path || pathname.startsWith(`${t.path}/`)),
    })),
  ];

  return (
    <section className="flow-stepbar" aria-label={`LinkedIn-Prozess, Phase ${phase.index} von ${FLOW_PHASE_COUNT}`}>
      <div className="flow-stepbar__row">
        <Link to="/gesamtprozess" className="flow-stepbar__home" title="Zur Übersicht des LinkedIn-Studios">
          <Icon name="linkedin" size={14} /> LinkedIn-Studio
        </Link>
        <span className="flow-stepbar__pos">
          Phase {phase.index} von {FLOW_PHASE_COUNT} · {phase.label}
        </span>
        <ol className="flow-dots" aria-label="Fortschritt">
          {FLOW_PHASES.map((p) => {
            const ps = phaseState(status, p);
            return (
              <li key={p.id}>
                <Link
                  to={phasePath(p)}
                  className={`flow-dot flow-dot--${ps.state}${p.id === phase.id ? ' is-current' : ''}`}
                  aria-current={p.id === phase.id ? 'step' : undefined}
                  title={`${p.index} · ${p.label}`}
                  aria-label={`${p.index} · ${p.label}`}
                >
                  {ps.state === 'done' ? <Icon name="check" size={10} /> : p.index}
                </Link>
              </li>
            );
          })}
        </ol>
        <div className="flow-stepbar__nav">
          {extraActions}
          {prevPhase ? (
            <Link to={phasePath(prevPhase)} className="btn btn-ghost btn-sm" title={prevPhase.description}>
              <Icon name="arrow-left" size={14} /> {prevPhase.index} · {prevPhase.label}
            </Link>
          ) : null}
          {nextPhase ? (
            <Link to={phasePath(nextPhase)} className="btn btn-ghost btn-sm" title={nextPhase.description}>
              {nextPhase.index} · {nextPhase.label} <Icon name="arrow-right" size={14} />
            </Link>
          ) : null}
        </div>
      </div>

      {tabs.length > 1 && (
        <nav className="flow-tabs" aria-label={`Schritte der Phase ${phase.label}`}>
          {tabs.map((t) => (
            <Link
              key={t.key}
              to={t.path}
              className={`flow-tab${t.current ? ' is-current' : ''}${t.done ? ' is-done' : ''}`}
              aria-current={t.current ? 'page' : undefined}
              title={t.title}
            >
              {t.done && <Icon name="check" size={12} />}
              {t.label}
            </Link>
          ))}
        </nav>
      )}

      {step && st && loaded && st.state === 'blocked' && st.missing.length > 0 && (
        <div className="flow-banner flow-banner--blocked" role="status">
          <Icon name="lock" size={16} />
          <span>
            Für <strong>{step.label}</strong> brauchst du zuerst{' '}
            {st.missing.map((m, i) => (
              <React.Fragment key={m.id}>
                {i > 0 && ' und '}
                <strong>{m.label}</strong>
              </React.Fragment>
            ))}
            .
          </span>
          <Link to={st.missing[0].path} className="btn btn-primary btn-sm">
            {st.missing[0].cta} <Icon name="arrow-right" size={14} />
          </Link>
        </div>
      )}

      {step && st && loaded && st.state === 'done' && (
        <div className="flow-banner flow-banner--done" role="status">
          <Icon name="check" size={16} />
          <span>{step.doneLabel(st.count)}.</span>
          {nextStep && (
            <Link to={nextStep.path} className="btn btn-primary btn-sm">
              Weiter zu {nextStep.label} <Icon name="arrow-right" size={14} />
            </Link>
          )}
        </div>
      )}

      {step && st && loaded && st.hints.length > 0 && st.state !== 'blocked' && (
        <ul className="flow-hints">
          {st.hints.map((h) => (
            <li key={h}><Icon name="info" size={12} /> {h}</li>
          ))}
        </ul>
      )}

      {!loaded && loading && <div className="flow-stepbar__loading" aria-hidden="true" />}
    </section>
  );
};

/** Schmale Variante fuer ergaenzende Werkzeuge (Flyer, Memes, Lead Magnets). */
export const FlowSideBar: React.FC<{ label: string }> = ({ label }) => (
  <div className="flow-stepbar flow-stepbar--side">
    <div className="flow-stepbar__row">
      <Link to="/gesamtprozess" className="flow-stepbar__home">
        <Icon name="linkedin" size={14} /> LinkedIn-Studio
      </Link>
      <span className="flow-stepbar__pos">Ergänzender Schritt · {label}</span>
      <div className="flow-stepbar__nav">
        <Link to="/gesamtprozess" className="btn btn-ghost btn-sm">
          Zur Übersicht <Icon name="arrow-right" size={14} />
        </Link>
      </div>
    </div>
  </div>
);

export default FlowStepBar;
